import { calculateIntervalMinutes } from './calculateIntervalMinutes';

interface TimeSlot {
  startTime: Date;
  endTime: Date;
}

/**
 * Generate exactly `numSlices` intervals from `startTime` to `endTime`.
 * The last slot might be shorter to exactly fit the end time.
 */
export function generateTimeSlots(
  startTime: Date,
  endTime: Date,
  numSlices: number,
): TimeSlot[] {
  if (numSlices <= 0) return [];
  const slots: TimeSlot[] = [];
  const intervalMinutes = calculateIntervalMinutes(
    startTime,
    endTime,
    numSlices,
  );
  if (intervalMinutes === 0) {
    return [];
  }
  let current = new Date(startTime);
  for (let i = 0; i < numSlices; i++) {
    const slotEnd = new Date(current.getTime() + intervalMinutes * 60000);

    // If we're on the last slice or would exceed endTime, use endTime as the end
    if (i === numSlices - 1 || slotEnd > endTime) {
      slots.push({
        startTime: new Date(current),
        endTime: new Date(endTime),
      });
      break;
    }

    slots.push({
      startTime: new Date(current),
      endTime: slotEnd,
    });
    current = slotEnd;
  }

  // If we haven't generated enough slots yet, continue with regular intervals
  if (slots.length < numSlices && slots.length > 0) {
    let lastEndTime = slots[slots.length - 1].endTime;
    while (slots.length < numSlices) {
      const nextStart = new Date(lastEndTime);
      const nextEnd = new Date(nextStart.getTime() + intervalMinutes * 60000);

      // If next end would exceed the total end time, use end time instead
      if (nextEnd > endTime) {
        if (nextStart < endTime) {
          slots.push({
            startTime: nextStart,
            endTime: new Date(endTime),
          });
        }
        break;
      }

      slots.push({
        startTime: nextStart,
        endTime: nextEnd,
      });
      lastEndTime = nextEnd;
    }
  }

  return slots;
}
